import React from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useAuth } from '../../../context/AuthContext'
import { IS_DEMO } from '../../../constants/env'
import { ErrorBoundaryWithSupport } from '../../../components/ErrorBoundary'
import LoadingSpinner from '../../../components/LoadingSpinner'
import WshHelpTrigger from '@wsh/WshHelpTrigger'
import NotificationBanner from '@design-components/NotificationBanner'
import {
  colors,
  gradients,
  PrimaryButton,
  transitions
} from '../../../theme/sharedComponents'

// ----------------------------------------------------------------------------
// Styled Components
// ----------------------------------------------------------------------------

const PageWrapper = styled.div`
  flex: 1;
  background: ${colors.backgroundAlt};
`

const PortalHeader = styled.div`
  background: white;
  border-bottom: 1px solid ${colors.borderLight};
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 1.5rem 2rem 0.75rem;
`

const TitleGroup = styled.div`
  display: flex;
  align-items: baseline;
  gap: 1rem;
  flex-wrap: wrap;
`

const PageTitle = styled.h1`
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0;
  background: ${gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`

const WelcomeText = styled.span`
  color: ${colors.textLightModeMuted};
  font-size: 0.9rem;
`

const TabNavigation = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  border-bottom: 2px solid ${colors.borderLight};
  padding: 0 0.75rem;
  align-items: flex-end;
`

const TabLink = styled(NavLink)`
  background: transparent;
  color: ${colors.textLightModeMuted};
  border: 2px solid transparent;
  border-bottom: none;
  border-radius: 6px 6px 0 0;
  margin-bottom: -2px;
  padding: 0.55rem 1.1rem;
  font-weight: 600;
  font-size: 0.85rem;
  text-decoration: none;
  transition: ${transitions.fast};
  display: flex;
  align-items: center;
  gap: 0.4rem;

  i {
    font-size: 0.85rem;
  }

  &:hover {
    color: ${colors.primary};
    background: ${colors.hoverTint};
  }

  &.active {
    color: ${colors.primary};
    background: ${colors.hoverTint};
    border-color: ${colors.borderLight};
    border-bottom-color: white;
  }

  @media (max-width: 576px) {
    flex: 1;
    justify-content: center;
    padding: 0.65rem 0.5rem;

    span {
      display: none;
    }

    i {
      font-size: 1.1rem;
    }
  }
`

const PageContent = styled.div`
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 1.5rem 1.5rem 6rem;

  @media (max-width: 576px) {
    padding: 1rem 0.75rem 6rem;
  }
`

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

const CustomerLayout: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()

  return (
    <PageWrapper>
      {IS_DEMO && (
        <NotificationBanner
          variant="accent"
          message={<>You're exploring a live read-only demo, nothing you do here affects real data. <a href="https://computeranything.dev/contact?projectType=wholesale-hub-demo" target="_blank" rel="noopener noreferrer">Get your own instance →</a></>}
        />
      )}
      <PortalHeader>
        <HeaderRow>
          <TitleGroup>
            <PageTitle>
              <i className="bi bi-person-circle"></i> Customer Portal
            </PageTitle>
            <WelcomeText>Welcome back, {user?.first_name || 'Customer'}</WelcomeText>
          </TitleGroup>
          <PrimaryButton data-tour="customer-shop-btn" onClick={() => navigate('/products')}>
            <i className="bi bi-bag-check"></i>
            Shop Now
          </PrimaryButton>
        </HeaderRow>
        <TabNavigation data-tour="customer-nav">
          <TabLink to="/customer-portal" end>
            <i className="bi bi-speedometer2"></i><span>Overview</span>
          </TabLink>
          <TabLink to="/customer-portal/orders" data-tour="tab-customer-orders">
            <i className="bi bi-receipt"></i><span>Orders</span>
          </TabLink>
          <TabLink to="/customer-portal/notifications">
            <i className="bi bi-bell"></i><span>Notifications</span>
          </TabLink>
          <TabLink to="/customer-portal/settings/account">
            <i className="bi bi-person-gear"></i><span>Settings</span>
          </TabLink>
        </TabNavigation>
      </PortalHeader>

      <PageContent>
        <ErrorBoundaryWithSupport dashboardHref="/customer-portal">
          <React.Suspense fallback={<LoadingSpinner />}>
            <Outlet />
          </React.Suspense>
        </ErrorBoundaryWithSupport>
      </PageContent>
      <WshHelpTrigger />
    </PageWrapper>
  )
}

export default CustomerLayout
